import { yupResolver } from '@hookform/resolvers/yup';
import { Typography } from '@material-ui/core';
import { accessHistoryApi } from 'api/authApi';
import {
  getCompanyLogosApi, getCurrentCourseCodeApi,
  getDesiredAreaApi, getDesiredCareApi, getDesiredCourseApi, getDesiredServiceApi, getDesiredStoreApi,
} from 'api/onlineBookingV2Api';
import clsx from 'clsx';
import ScreenLoader from 'components/organismos/ScreenLoader';
import { getTextGender } from 'constants/reservation';
import useAppSnackbar from 'hooks/useAppSnackbar';
import BigButtonNext from 'pages/reservationBookingOnlineV2/components/BigButtonNext';
import DesiredStoreOptions from 'pages/reservationBookingOnlineV2/components/DesiredStoreOptions';
import SelectOption from 'pages/reservationBookingOnlineV2/components/SelectOption';
import { IDefaultValueSearch, useReservationBookingOnlineContext } from 'pages/reservationBookingOnlineV2/context';
import { formatSelectOptionList, returnHistoryPath } from 'pages/reservationBookingOnlineV2/functions';
import React, {
  ChangeEvent, useCallback, useEffect, useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import handleError from 'utility/handleError';
import * as yup from 'yup';
import SectionHeader from '../../components/SectionHeader';
import useStyles from './styles';
import {
  IReservationSearch, ISelectOption, TDesiredAreaParam, TDesiredCare, TDesiredCareParam, TDesiredCourse, TDesiredCourseParam, TDesiredStoreParam,
} from './types';

interface IStepsBeforeReservation {
  companyCode: string,
  courseCode?: string,
  mediaCode?:string,
  gender?: string,
  serviceCode?: string,
  trackingCode?: string,
}

const getTextByIdInListData = (arr:ISelectOption[], id:string | string) => arr?.filter((item) => item?.id === id)[0]?.text || '';

const StepsBeforeReservation : React.FC<IStepsBeforeReservation> = ({
  companyCode, courseCode, mediaCode, gender, serviceCode, trackingCode,
}) => {
  const hasUrlParams = !!(gender && serviceCode && courseCode);
  const [isLoading, setLoading] = useState(false);
  const [serviceCodeMatched, setServiceCodeMatched] = useState(false);
  const [courseCodeMatched, setCourseCodeMatched] = useState(false);
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useAppSnackbar();
  const {
    handleChangeSearch, chosenDesiredService, chosenDesiredCare, chosenCourse, chosenArea, chosenStore,
  } = useReservationBookingOnlineContext();
  const [defaultValueSearch, setDefaultValueSearch] = useState<IDefaultValueSearch>({
    chosenDesiredService, chosenDesiredCare, chosenCourse, chosenArea, chosenStore,
  });
  const [desiredServiceList, setDesiredServiceList] = useState<ISelectOption[]>([]);
  const [desiredCareList, setDesiredCareList] = useState<ISelectOption[]>([]);
  const [desiredCareListRaw, setDesiredCareListRaw] = useState<TDesiredCare[]>([]);
  const [desiredCourseList, setDesiredCourseList] = useState<ISelectOption[]>([]);
  const [desiredCourseListRaw, setDesiredCourseListRaw] = useState<TDesiredCourse[]>([]);
  const [desiredAreaList, setDesiredAreaList] = useState<ISelectOption[]>([]);
  const [logoUrls, setLogoUrls] = useState<string[]>([]);
  const [desiredStoreList, setDesiredStoreList] = useState<ISelectOption[]>([]);

  const handleSelect = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    switch (name) {
      case 'chosenDesiredService':
        setDefaultValueSearch((prevState) => ({
          ...prevState, chosenDesiredService: value || '', chosenDesiredCare: '', chosenCourse: '', chosenArea: '', chosenStore: '',
        }));
        break;
      case 'chosenDesiredCare':
        setDefaultValueSearch((prevState) => ({
          ...prevState, chosenDesiredCare: value, chosenCourse: '', chosenArea: '', chosenStore: '',
        }));
        break;
      case 'chosenCourse':
        setDefaultValueSearch((prevState) => ({
          ...prevState, chosenCourse: value, chosenArea: '', chosenStore: '',
        }));
        break;
      case 'chosenArea':
        setDefaultValueSearch((prevState) => ({
          ...prevState, chosenArea: value, chosenStore: '',
        }));
        break;
      default:
        break;
    }
  }, []);

  const handleClickStoreOption = useCallback((value:string) => {
    setDefaultValueSearch((prevState) => ({
      ...prevState, chosenStore: value,
    }));
  }, []);

  const validationSchema = yup.object().shape<IReservationSearch>({
    chosenDesiredService: yup.string().required('名を入力してください。').oneOf(['1', '2'], '名を入力してください。'),
    chosenDesiredCare: yup.string().required('名を入力してください。'),
    chosenCourse: yup.string().required('名を入力してください。'),
    chosenArea: yup.string().required('名を入力してください。'),
    chosenStore: yup.string(),
  });

  const methods = useForm<IReservationSearch>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      chosenDesiredService: '',
      chosenDesiredCare: '',
      chosenCourse: '',
      chosenArea: '',
      chosenStore: '',
    },
  });

  const { handleSubmit, getValues, setValue } = methods;

  const onSubmit = (data: IReservationSearch) => {
    if (!defaultValueSearch?.chosenStore) {
      enqueueSnackbar('名を入力してください。', { variant: 'error' });
      return;
    }
    handleChangeSearch({
      chosenDesiredService: data?.chosenDesiredService || '',
      chosenDesiredCare: data?.chosenDesiredCare,
      chosenCourse: data?.chosenCourse,
      chosenArea: data?.chosenArea,
      chosenStore: defaultValueSearch?.chosenStore,
      chosenDesiredServiceName: getTextByIdInListData(desiredServiceList, data?.chosenDesiredService),
      chosenDesiredCareName: getTextByIdInListData(desiredCareList, data?.chosenDesiredCare),
      chosenCourseName: getTextByIdInListData(desiredCourseList, data?.chosenCourse),
      chosenAreaName: getTextByIdInListData(desiredAreaList, data?.chosenArea),
      chosenStoreName: getTextByIdInListData(desiredStoreList, defaultValueSearch?.chosenStore),
      chosenCourseAcceptRequestReservation: desiredCourseListRaw.find((c) => c.contractCourseMstId === data?.chosenCourse)?.acceptRequestReservation ?? false,
    });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'reservation_service_selected',
      reservation_company_code: companyCode,
      reservation_org_id: defaultValueSearch?.chosenStore || '',
      reservation_org_name: getTextByIdInListData(desiredStoreList, defaultValueSearch?.chosenStore),
      reservation_service: getTextByIdInListData(desiredServiceList, data?.chosenDesiredService),
      reservation_care: getTextByIdInListData(desiredCareList, data?.chosenDesiredCare),
      reservation_course: getTextByIdInListData(desiredCourseList, data?.chosenCourse),
      reservation_area: getTextByIdInListData(desiredAreaList, data?.chosenArea),
      reservation_store: getTextByIdInListData(desiredStoreList, defaultValueSearch?.chosenStore),
    });
    history.replace({
      search: returnHistoryPath(companyCode, 2, courseCode, mediaCode, gender, serviceCode, trackingCode),
    });
  };

  // List Handle Call Api Get List Dropdown
  // ご希望のサービス
  const getDesiredServiceList = useCallback(async (argument:string, includeHidden?: boolean) => {
    try {
      setLoading(true);
      const res = await getDesiredServiceApi(argument, includeHidden);
      const options = formatSelectOptionList(res, 'code', 'value');
      setDesiredServiceList(options);
    } catch (error: any) {
      handleError({ error, enqueueSnackbar });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // ご希望のお手入れ
  const getDesiredCareList = useCallback(async (argument:TDesiredCareParam) => {
    try {
      const res = await getDesiredCareApi(argument);
      const options = formatSelectOptionList(res, 'contractCourseCategoryId', 'courseCategoryName');
      setDesiredCareList(options);
      setDesiredCareListRaw(res || []);
    } catch (error: any) {
      handleError({ error, enqueueSnackbar });
    }
  }, [enqueueSnackbar]);

  // ご希望のコース
  const getDesiredCourseList = useCallback(async (argument:TDesiredCourseParam) => {
    try {
      const res = await getDesiredCourseApi(argument);
      const options = formatSelectOptionList(res, 'contractCourseMstId', 'courseName');
      setDesiredCourseList(options);
      setDesiredCourseListRaw(res || []);
    } catch (error: any) {
      handleError({ error, enqueueSnackbar });
    }
  }, [enqueueSnackbar]);

  // ご希望のエリア
  const getDesiredAreaList = useCallback(async (argument: TDesiredAreaParam) => {
    try {
      const res = await getDesiredAreaApi(argument);
      const options = formatSelectOptionList(res, 'areaId', 'areaName');
      setDesiredAreaList(options);
    } catch (error: any) {
      handleError({ error, enqueueSnackbar });
    }
  }, [enqueueSnackbar]);

  // ご希望の店舗
  const getDesiredStoreList = useCallback(async (argument:TDesiredStoreParam) => {
    try {
      const res = await getDesiredStoreApi(argument);
      const options = formatSelectOptionList(res, 'orgId', 'orgName');
      setDesiredStoreList(options);
    } catch (error: any) {
      handleError({ error, enqueueSnackbar });
    }
  }, [enqueueSnackbar]);

  // is page have courseCode in param
  const getCurrentCourseCode = useCallback(async (params:{ companyCode: string, courseCode: string }) => {
    try {
      setLoading(true);
      const res = await getCurrentCourseCodeApi(params);
      setDesiredServiceList([{ id: String(res?.classType?.code), text: res?.classType?.value }]);
      setValue('chosenDesiredService', String(res?.classType?.code));
      setValue('chosenDesiredCare', res?.contractCourseCategoryId);
      setValue('chosenCourse', res?.contractCourseMstId);
      setDefaultValueSearch((prevState) => (
        {
          ...prevState,
          chosenDesiredService: String(res?.classType?.code),
          chosenDesiredCare: res?.contractCourseCategoryId,
          chosenCourse: res?.contractCourseMstId,
        }
      ));
    } catch (error: any) {
      handleError({ error, enqueueSnackbar });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, setValue]);

  useEffect(() => {
    // Save history access screen
    try {
      const paramsPost = { accessUrl: `${history?.location?.pathname + history?.location?.search}`, companyCode, staffId: '' };
      accessHistoryApi(paramsPost);
    } catch (error) {
      handleError({ error, enqueueSnackbar });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // 企業ロゴ取得
    if (!companyCode) return;
    const fetchLogos = async () => {
      try {
        const urls = await getCompanyLogosApi(companyCode);
        setLogoUrls(urls || []);
      } catch (error) {
        // ロゴ取得失敗は無視（表示しないだけ）
      }
    };
    fetchLogos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode]);

  useEffect(() => {
    // ご希望のサービス
    if (!companyCode) return;
    if (courseCode && !gender && !serviceCode) {
      getCurrentCourseCode({ companyCode, courseCode });
    } else {
      getDesiredServiceList(companyCode, hasUrlParams);
      const isValidGender = gender === '1' || gender === '2';
      if (isValidGender) {
        setValue('chosenDesiredService', gender);
        setDefaultValueSearch((prev) => ({ ...prev, chosenDesiredService: gender }));
      } else {
        setValue('chosenDesiredService', defaultValueSearch?.chosenDesiredService || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode, courseCode, gender, getCurrentCourseCode, getDesiredServiceList, setValue]);

  useEffect(() => {
    // ご希望のお手入れ
    if (!defaultValueSearch?.chosenDesiredService) {
      setDesiredCareList([]);
      setValue('chosenDesiredCare', '');
      return;
    }
    getDesiredCareList({
      companyCode,
      classType: getTextGender(defaultValueSearch.chosenDesiredService),
      ...(hasUrlParams ? { includeHidden: true } : {}),
    });
    setValue('chosenDesiredCare', defaultValueSearch?.chosenDesiredCare || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode, defaultValueSearch.chosenDesiredService, getDesiredCareList, setValue]);

  useEffect(() => {
    // serviceCode パラメータがある場合、サービスリストからマッチするアイテムを自動選択
    if (!serviceCode || !desiredCareListRaw.length) return;
    const matchedCare = desiredCareListRaw.find(
      (care) => care.courseCategoryCode === serviceCode,
    );
    if (matchedCare) {
      setValue('chosenDesiredCare', matchedCare.contractCourseCategoryId);
      setDefaultValueSearch((prev) => ({
        ...prev,
        chosenDesiredCare: matchedCare.contractCourseCategoryId,
      }));
      setServiceCodeMatched(true);
    } else {
      setServiceCodeMatched(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCode, desiredCareListRaw, courseCode, setValue]);

  useEffect(() => {
    // ご希望のコース
    if (!defaultValueSearch?.chosenDesiredService || !defaultValueSearch?.chosenDesiredCare) {
      setDesiredCourseList([]);
      setValue('chosenCourse', '');
      return;
    }
    getDesiredCourseList({
      companyCode,
      classType: getTextGender(defaultValueSearch.chosenDesiredService),
      contractCourseCategoryId: defaultValueSearch?.chosenDesiredCare,
      ...(hasUrlParams ? { includeHidden: true } : {}),
    });
    setValue('chosenCourse', defaultValueSearch?.chosenCourse || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode, defaultValueSearch.chosenDesiredCare, defaultValueSearch.chosenDesiredService, getDesiredCourseList, setValue]);

  useEffect(() => {
    // courseCode パラメータがある場合、コースリストからマッチするアイテムを自動選択
    if (!courseCode || !desiredCourseListRaw.length) return;
    const matchedCourse = desiredCourseListRaw.find(
      (course) => course.courseCode === courseCode,
    );
    if (matchedCourse) {
      setValue('chosenCourse', matchedCourse.contractCourseMstId);
      setDefaultValueSearch((prev) => ({
        ...prev,
        chosenCourse: matchedCourse.contractCourseMstId,
      }));
      setCourseCodeMatched(true);
    } else {
      setCourseCodeMatched(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseCode, desiredCourseListRaw, setValue]);

  useEffect(() => {
    // ご希望のエリア
    if (!defaultValueSearch?.chosenCourse) {
      setDesiredAreaList([]);
      setValue('chosenArea', '');
      return;
    }
    getDesiredAreaList({
      companyCode,
      contractCourseMstId: defaultValueSearch?.chosenCourse,
    });
    setValue('chosenArea', defaultValueSearch?.chosenArea || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCode, defaultValueSearch.chosenCourse, getDesiredAreaList, setValue]);

  useEffect(() => {
    // ご希望の店舗
    if (!defaultValueSearch?.chosenCourse || !defaultValueSearch?.chosenArea) {
      setDesiredStoreList([]);
      return;
    }
    getDesiredStoreList({
      companyCode,
      contractCourseMstId: defaultValueSearch?.chosenCourse,
      areaId: defaultValueSearch?.chosenArea,
    });
  }, [companyCode, defaultValueSearch.chosenArea, defaultValueSearch.chosenCourse, getDesiredStoreList]);


  return (
    <ScreenLoader isLoading={isLoading}>
      {/* Steps before reservation */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.root}>
            {logoUrls.length > 0 && (
              <div className={classes.logoContainer}>
                {logoUrls.map((url) => (
                  <img
                    key={url}
                    src={url}
                    alt=""
                    className={classes.logoImage}
                  />
                ))}
              </div>
            )}
            <div className={classes.stepsHeader}>
              <Typography className={classes.stepsHeaderMain}>
                {'たった1分\u3000簡単3ステップ予約！'}
              </Typography>
            </div>
            <div className={classes.stepsFlow}>
              <div className={classes.stepFirst}>①コース・店舗を選ぶ</div>
              <div className={classes.stepArrow} aria-hidden="true" />
              <div className={classes.stepMiddle}>②日時を選ぶ</div>
              <div className={classes.stepArrow} aria-hidden="true" />
              <div className={classes.stepLast}>③お客様情報を入力</div>
            </div>
            <SectionHeader title="1. ご希望のサービス・サロンの選択" />
            <div className={classes.desireServiceSection}>
              {/* Desired Service */}
              <div className={classes.serviceItem}>
                <div className={classes.serviceLabel}>
                  <p>性別：</p>
                </div>
                <SelectOption
                  handleChange={handleSelect}
                  name="chosenDesiredService"
                  list={desiredServiceList}
                  isSetSelectOptionDefaultFirst={true}
                  disabled={(desiredServiceList?.length === 0) || !!courseCode || serviceCodeMatched || gender === '1' || gender === '2'}
                  valueSetSelectOptionDefaultFirst=""
                  defaultOption={defaultValueSearch?.chosenDesiredService || ''}
                />
              </div>
              {/* Desired care */}
              <div className={classes.serviceItem}>
                <div className={classes.serviceLabel}>
                  <p>ご希望のサービス：</p>
                </div>
                <SelectOption
                  handleChange={handleSelect}
                  name="chosenDesiredCare"
                  list={desiredCareList}
                  isSetSelectOptionDefaultFirst={true}
                  disabled={!getValues('chosenDesiredService') || !!courseCode || serviceCodeMatched}
                  valueSetSelectOptionDefaultFirst=""
                  defaultOption={defaultValueSearch?.chosenDesiredCare || ''}
                />
              </div>
              {/* Desired care */}
              <div className={classes.serviceItem}>
                <div className={classes.serviceLabel}>
                  <p>ご希望のコース：</p>
                </div>
                <SelectOption
                  handleChange={handleSelect}
                  name="chosenCourse"
                  list={desiredCourseList}
                  isSetSelectOptionDefaultFirst={true}
                  disabled={!getValues('chosenDesiredCare') || courseCodeMatched}
                  valueSetSelectOptionDefaultFirst=""
                  defaultOption={defaultValueSearch?.chosenCourse || ''}
                />
              </div>
              {/* Desired care */}
              <div className={classes.serviceItem}>
                <div className={classes.serviceLabel}>
                  <p>ご希望のエリア：</p>
                </div>
                <SelectOption
                  handleChange={handleSelect}
                  name="chosenArea"
                  list={desiredAreaList}
                  isSetSelectOptionDefaultFirst={true}
                  disabled={!getValues('chosenCourse')}
                  valueSetSelectOptionDefaultFirst=""
                  defaultOption={defaultValueSearch?.chosenArea || ''}
                />
              </div>
              {/* Store of your choice */}
              <div className={classes.serviceItem}>
                <div className={clsx(classes.serviceLabel, classes.serviceLabelStretch)}>
                  <p>ご希望の店舗：</p>
                </div>
                <DesiredStoreOptions title="ご希望のサロンをお選びください" disabled={!getValues('chosenArea')} list={desiredStoreList} defaultOption={defaultValueSearch?.chosenStore} handleClick={handleClickStoreOption} />
              </div>
            </div>
            {/* Button  */}
            <div className={classes.buttonContainer}>
              <BigButtonNext content="ご来店日時選択へ" type="submit" isDisabled={!defaultValueSearch?.chosenStore} />
            </div>
          </div>
        </form>
      </FormProvider>
    </ScreenLoader>
  );
};

export default StepsBeforeReservation;
